<template>
  <main class="page">
    <section class="home-hero" aria-labelledby="home-title">
      <div class="hero-inner">
        <div class="hero-copy">
          <div class="hero-kicker">社区零售应用矩阵</div>
          <h1 id="home-title">冷丰生态</h1>
          <p>以冷丰消费者端和丰银宝店铺端连接社区居民、门店、店长与商品履约。</p>
          <div class="hero-actions">
            <router-link class="button primary" to="/products/lengfeng">查看冷丰</router-link>
            <router-link class="button secondary" to="/products/fengyinbao">查看丰银宝</router-link>
          </div>
        </div>
        <div class="hero-visual" aria-label="冷丰应用界面截图">
          <PhoneFrame
            class="hero-phone secondary-shot"
            :screenshot="lengfeng.screenshots[1]"
          />
          <PhoneFrame
            class="hero-phone primary-shot"
            :screenshot="lengfeng.screenshots[0]"
          />
        </div>
      </div>
    </section>

    <section class="section" id="products">
      <div class="container">
        <SectionHead
          eyebrow="产品矩阵"
          title="两个 App，服务社区零售的前台和后台。"
          description="冷丰面向社区消费者，丰银宝面向门店经营者；两个产品围绕商品、订单、直播和履约形成完整业务闭环。"
        />

        <div class="product-cards">
          <article v-for="product in products" :key="product.key" class="product-card">
            <div class="product-card-head">
              <img :src="product.logo" :alt="`${product.name} Logo`" />
              <div>
                <p>{{ product.kicker }}</p>
                <h2>{{ product.name }}</h2>
              </div>
            </div>
            <p class="product-description">{{ product.longDescription }}</p>
            <div class="highlight-list">
              <span v-for="item in product.highlights" :key="item.label">
                <strong>{{ item.value }}</strong>
                {{ item.label }}
              </span>
            </div>
            <div class="card-actions">
              <router-link class="button primary" :to="`/products/${product.key}`">
                产品详情
              </router-link>
              <router-link class="button secondary" :to="`/download/${product.key}`">
                下载版本
              </router-link>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="section alt">
      <div class="container">
        <SectionHead
          eyebrow="业务链路"
          title="从用户下单到门店履约，保持一条清晰的协同链路。"
          description="消费者端负责发现商品、观看直播、下单支付；店铺端负责接单、备货、库存、配送自提和售后处理。"
        />

        <div class="flow-grid">
          <article>
            <span>01</span>
            <h3>消费者发现商品</h3>
            <p>冷丰承接社区首页、商品分类、搜索推荐和直播入口。</p>
          </article>
          <article>
            <span>02</span>
            <h3>直播和活动转化</h3>
            <p>店长通过直播、福利活动和商品讲解建立社区信任。</p>
          </article>
          <article>
            <span>03</span>
            <h3>门店接单履约</h3>
            <p>丰银宝聚合订单、拣货、配送、自提和售后任务。</p>
          </article>
          <article>
            <span>04</span>
            <h3>数据沉淀复盘</h3>
            <p>订单、商品、会员和直播表现沉淀为后续经营依据。</p>
          </article>
        </div>
      </div>
    </section>

    <section class="section" id="about">
      <div class="container">
        <SectionHead
          eyebrow="关于我们"
          :title="companyInfo.slogan"
          description="冷丰围绕社区零售、直播带货和门店履约场景，持续完善便捷、可信、贴近生活的线上购物体验。"
        />

        <div class="about-layout">
          <article class="about-panel">
            <h3>公司信息</h3>
            <p>{{ companyInfo.description }}</p>
            <p>{{ companyInfo.secondaryDescription }}</p>
          </article>

          <aside class="about-panel" aria-labelledby="contact-title">
            <h3 id="contact-title">联系方式</h3>
            <dl class="contact-list">
              <div>
                <dt>公司全称</dt>
                <dd>{{ companyInfo.name }}</dd>
              </div>
              <div>
                <dt>联系邮箱</dt>
                <dd>{{ companyInfo.email }}</dd>
              </div>
              <div>
                <dt>联系电话</dt>
                <dd>{{ companyInfo.phone }}</dd>
              </div>
            </dl>
          </aside>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { companyInfo } from '@/data/company'
import { products } from '@/data/products'
import PhoneFrame from '@/components/PhoneFrame.vue'
import SectionHead from '@/components/SectionHead.vue'

export default {
  name: 'HomeView',
  components: {
    PhoneFrame,
    SectionHead
  },
  data() {
    return {
      companyInfo,
      products
    }
  },
  computed: {
    lengfeng() {
      return this.products.find((product) => product.key === 'lengfeng')
    }
  }
}
</script>

<style scoped lang="scss">
.home-hero {
  position: relative;
  overflow: hidden;
  min-height: 660px;
  display: grid;
  align-items: center;
  background:
    linear-gradient(90deg, rgba(50, 18, 8, 0.9), rgba(31, 122, 91, 0.62)),
    linear-gradient(135deg, #ff8a00 0%, #ff4b0b 46%, #2a9d8f 100%);
}

.hero-inner {
  width: min(1120px, calc(100% - 40px));
  margin: 0 auto;
  padding: 72px 0;
  display: grid;
  grid-template-columns: minmax(0, 0.78fr) minmax(420px, 0.72fr);
  align-items: center;
  gap: 48px;
}

.hero-kicker {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.86);
  font-size: 14px;
  font-weight: 700;

  &::before {
    content: "";
    width: 34px;
    height: 2px;
    background: #ff8a00;
  }
}

h1 {
  max-width: 720px;
  margin: 18px 0 16px;
  color: #fff;
  font-size: 72px;
  line-height: 1.04;
  letter-spacing: 0;
}

.hero-copy > p {
  max-width: 620px;
  margin: 0;
  color: rgba(255, 255, 255, 0.86);
  font-size: 20px;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 34px;

  .secondary {
    color: #fff;
    border-color: rgba(255, 255, 255, 0.42);
    background: rgba(255, 255, 255, 0.08);
  }
}

.hero-visual {
  position: relative;
  min-height: 540px;
}

.hero-phone {
  position: absolute;
}

.primary-shot {
  top: 0;
  right: 14px;
  width: 252px;
  transform: rotate(3deg);
}

.secondary-shot {
  left: 18px;
  bottom: 4px;
  width: 214px;
  transform: rotate(-6deg);
}

.product-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.product-card,
.flow-grid article,
.about-panel {
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
}

.product-card {
  padding: 26px;
  box-shadow: 0 16px 38px rgba(20, 32, 54, 0.06);
}

.product-card-head {
  display: flex;
  align-items: center;
  gap: 14px;

  img {
    width: 58px;
    height: 58px;
    border-radius: 22.5%;
    object-fit: cover;
  }

  p {
    margin: 0 0 2px;
    color: var(--muted);
    font-size: 13px;
  }

  h2 {
    margin: 0;
    font-size: 30px;
    line-height: 1.15;
  }
}

.product-description {
  min-height: 104px;
  margin: 18px 0;
  color: var(--muted);
}

.highlight-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 22px;

  span {
    min-height: 72px;
    padding: 12px;
    border-radius: 8px;
    color: #42506a;
    background: #f5f8fb;
  }

  strong {
    display: block;
    color: var(--ink);
    font-size: 20px;
    line-height: 1.2;
  }
}

.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.flow-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  article {
    min-height: 210px;
    padding: 22px;
  }

  span {
    display: inline-grid;
    width: 34px;
    height: 34px;
    place-items: center;
    margin-bottom: 18px;
    border-radius: 8px;
    color: #fff;
    font-size: 13px;
    font-weight: 800;
    background: var(--green);
  }

  h3 {
    margin: 0 0 8px;
    font-size: 20px;
    line-height: 1.3;
  }

  p {
    margin: 0;
    color: var(--muted);
  }
}

.about-layout {
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(300px, 0.58fr);
  gap: 18px;
}

.about-panel {
  padding: 28px;

  h3 {
    margin: 0 0 8px;
    font-size: 20px;
  }

  p {
    margin: 0;
    color: var(--muted);
  }

  p + p {
    margin-top: 16px;
  }
}

.contact-list {
  display: grid;
  gap: 14px;
  margin: 22px 0 0;

  div {
    padding-bottom: 14px;
    border-bottom: 1px solid var(--line);
  }

  div:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }

  dt {
    color: var(--muted);
    font-size: 13px;
  }

  dd {
    margin: 3px 0 0;
    font-weight: 800;
    word-break: break-word;
  }
}

@media (max-width: 900px) {
  .home-hero {
    min-height: 0;
  }

  .hero-inner {
    grid-template-columns: 1fr;
    padding: 62px 0 72px;
    gap: 34px;
  }

  h1 {
    font-size: 56px;
  }

  .hero-visual {
    min-height: 460px;
  }

  .primary-shot {
    right: 12%;
    width: 222px;
  }

  .secondary-shot {
    left: 12%;
    width: 188px;
  }

  .product-cards,
  .flow-grid,
  .about-layout {
    grid-template-columns: 1fr;
  }

  .product-description {
    min-height: 0;
  }

  .flow-grid article {
    min-height: 0;
  }
}

@media (max-width: 640px) {
  .hero-inner {
    width: min(100% - 32px, 1120px);
    padding: 46px 0 54px;
    gap: 24px;
  }

  h1 {
    margin-top: 14px;
    font-size: 44px;
  }

  .hero-copy > p {
    font-size: 17px;
  }

  .hero-visual {
    min-height: 470px;
  }

  .primary-shot {
    left: 50%;
    right: auto;
    width: min(210px, 60vw);
    transform: translateX(-50%) rotate(2deg);
  }

  .secondary-shot {
    display: none;
  }

  .highlight-list {
    grid-template-columns: 1fr;
  }

  .product-card,
  .flow-grid article,
  .about-panel {
    padding: 20px;
  }

  .product-card-head {
    align-items: flex-start;

    img {
      width: 50px;
      height: 50px;
    }

    h2 {
      font-size: 24px;
    }
  }

  .highlight-list span {
    min-height: 0;
  }
}

@media (max-width: 420px) {
  .hero-actions,
  .card-actions {
    width: 100%;
  }
}
</style>
